<template>
  <div class="chart-legend" data-testid="allocation-chart-legend">
    <template v-if="isLoading">
      <SkeletonLoader class="chart-legend__loader">
        <div
          v-for="index in 4"
          :key="index"
          class="chart-legend__accordion__loader"
        />
      </SkeletonLoader>
    </template>
    <template v-else-if="!hasError">
      <div
        v-for="(section, index) in sections"
        :key="section.name"
        :class="[
          'chart-legend__accordion-wrapper',
          { 'chart-legend__accordion-wrapper--opened': openSectionsMap[index] }
        ]"
      >
        <Accordion
          class="chart-legend__accordion"
          :should-add-text-style="false"
          :disabled="!verifyIfSectionHasItems(section)"
          @accordion-changed="
            isOpen => handleToggleAccordion(isOpen, index, section)
          "
        >
          <template #title>
            <div class="chart-legend__accordion__left-container">
              <div
                class="chart-legend__accordion__color"
                :style="{ backgroundColor: section.color }"
              />
              <div>
                <BaseText size="lg" :color="NebraskaColors.elementPrimary">{{
                  section.name
                }}</BaseText>
                <Money>
                  <BaseText
                    size="sm"
                    :color="NebraskaColors.elementSecondary"
                    >{{ section.balance.formatted }}</BaseText
                  >
                </Money>
              </div>
            </div>
          </template>
          <template #subtitle>
            <div class="chart-legend__accordion__right-container">
              <BaseText size="lg" :color="NebraskaColors.elementPrimary">{{
                section.percentageAllocatedOnPortfolio.formatted
              }}</BaseText>
              <BaseText
                v-if="section.quantity"
                size="sm"
                :color="NebraskaColors.elementSecondary"
                >{{ section.quantity.formatted }}</BaseText
              >
            </div>
          </template>
          <template>
            <div
              v-if="section.types && section.types.length > 0"
              class="chart-legend__accordion__content"
            >
              <StocksTable
                v-for="(type, typeIndex) in section.types"
                :key="typeIndex"
                class="chart-legend__accordion__stocks-table"
                :data="type"
              />
            </div>
          </template>
        </Accordion>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue';

import { BaseText, SkeletonLoader } from '@warrenbrasil/nebraska-web';
import { Accordion } from '@/modules/common/components/Accordion';
import { StocksTable } from './components/StocksTable';
import { Money } from '@/modules/common/components/MoneyV2';

import { PortfolioSection } from '@/modules/wealth/services/portfolio';
import { NebraskaColors } from '@warrenbrasil/nebraska-tokens-web';

interface Props {
  sections: PortfolioSection[];
  isLoading: boolean;
  hasError: boolean;
}
const props = defineProps<Props>();

const openSectionsMap = ref<Record<number, boolean>>({});

watch(
  () => props.sections,
  sections => {
    const newOpenSectionsMap: Record<number, boolean> = {};

    sections.forEach((_section, index) => {
      newOpenSectionsMap[index] = false;
    });

    openSectionsMap.value = newOpenSectionsMap;
  },
  { immediate: true }
);

const handleToggleAccordion = (
  isOpen: boolean,
  index: number,
  section: PortfolioSection
) => {
  if (!verifyIfSectionHasItems(section)) return;
  openSectionsMap.value[index] = isOpen;
};

const verifyIfSectionHasItems = (section: PortfolioSection) => {
  return section.types && section.types.length > 0;
};
</script>

<style lang="less" scoped>
.chart-legend {
  width: 100%;

  &__loader {
    display: flex;
    flex-direction: column;
    padding: 0 @size-spacing-x400;

    .from-md({
      padding: 0 @size-spacing-x800;
    });
  }

  &__accordion {
    max-width: 800px;
    margin: 0 auto;

    ::v-deep button {
      padding: @size-spacing-x400 0 !important;
    }

    &__loader {
      height: 53px;
      border-radius: @size-radius-x200;
      max-width: 800px;
      width: 100%;
      margin: @size-spacing-x400 auto;
    }

    &-wrapper {
      width: 100%;
      padding: 0 @size-spacing-x400;

      .from-md({
        padding: 0 @size-spacing-x800;
      });

      &--opened {
        background-color: @background-primary;
      }
    }

    &__left-container {
      display: flex;
      width: auto !important;
    }

    &__right-container {
      text-align: right;
    }

    &__color {
      width: 4px;
      border-radius: @size-radius-x100;
      margin-right: @size-spacing-x400;
    }

    &__content {
      padding-top: @size-spacing-x600;
      padding-bottom: @size-spacing-x800;
    }

    &__stocks-table {
      margin-bottom: @size-spacing-x300;
    }
  }
}
</style>
